import { BaseModel } from 'ag-common-lib/lib/models/base.model';
import { LookupBase } from 'ag-common-lib/lib/models/utils/lookup-base.model';
import { LookupKeys } from 'ag-common-lib/lib/models/crm/lookup.model';
import { DocumentWriteAction } from '../../lists/change-logs/document-write-actions.enum';
import { POLICY_TYPES_LOOKUP, PolicyTypes } from 'ag-common-lib/lib/models/utils/policy.model';
import { ChangeLogsConfig, ChangeLogsDataType } from 'ag-common-lib/lib/models/registration/change-logs.model';
import { AssociationKeys, AssociationMap } from 'ag-common-lib/lib/models/utils/association.model';

export const USER_AGREEMENTS_MANAGEMENT_ROOT_FILE_FOLDER = 'user-agreements' as const;

export const INITIAL_VERSION = '0.0.0';

export enum SupportedLanguages {
  en = 'en',
  pt = 'pt',
  es = 'es',
}

export const supportedLanguagesMap = new Map([
  [SupportedLanguages.en, 'English'],
  [SupportedLanguages.pt, 'Portuguese'],
  [SupportedLanguages.es, 'Spanish'],
]);

export const SUPPORTED_LANGUAGES_LOOKUP: LookupBase[] = Object.values(SupportedLanguages).map(value => {
  return { [LookupKeys.value]: value, [LookupKeys.description]: supportedLanguagesMap.get(value) };
});

export const SUPPORTED_LANGUAGES_SHORT_LOOKUP: LookupBase[] = Object.values(SupportedLanguages).map(value => {
  return { [LookupKeys.value]: value, [LookupKeys.description]: value.toUpperCase() };
});

export enum UserAgreementStatus {
  draft = 'draft',
  published = 'published',
  archived = 'archived',
  active = 'active',
}

export const userAgreementStatusesMap = new Map([
  [UserAgreementStatus.draft, 'Draft'],
  [UserAgreementStatus.published, 'Published'],
  [UserAgreementStatus.archived, 'Archived'],
  [UserAgreementStatus.active, 'Active'],
]);

export const USER_AGREEMENT_STATUSES_LOOKUP: LookupBase[] = Object.values(UserAgreementStatus).map(value => {
  return { [LookupKeys.value]: value, [LookupKeys.description]: userAgreementStatusesMap.get(value) };
});

export enum UserAgreementVersionChangeKey {
  major = 'major',
  middle = 'middle',
  minor = 'minor',
}

export const userAgreementVersionChangeMap = new Map([
  [UserAgreementVersionChangeKey.major, 'Major'],
  [UserAgreementVersionChangeKey.middle, 'Middle'],
  [UserAgreementVersionChangeKey.minor, 'Minor'],
]);

export const USER_AGREEMENT_VERSION_CHANGE_LOOKUP: LookupBase[] = Object.values(UserAgreementVersionChangeKey).map(
  value => {
    return { [LookupKeys.value]: value, [LookupKeys.description]: userAgreementVersionChangeMap.get(value) };
  },
);

export enum UserAgreementSectionKeys {
  id = 'id',
  title = 'title',
  description = 'description',
}

export class UserAgreementSection {
  [UserAgreementSectionKeys.id]: string;
  [UserAgreementSectionKeys.title]: string;
  [UserAgreementSectionKeys.description]: string;
}

export enum UserAgreementHistoryKeys {
  userAgreementDbId = 'userAgreementDbId',
  message = 'message',
  wasabiPath = 'wasabiPath',
  action = 'action',
}

export class UserAgreementHistory extends BaseModel {
  [UserAgreementHistoryKeys.userAgreementDbId]: string;
  [UserAgreementHistoryKeys.message]?: string;
  [UserAgreementHistoryKeys.wasabiPath]: string;
  [UserAgreementHistoryKeys.action]?: DocumentWriteAction;
}

export const userAgreementHistoryMap = new Map([
  [UserAgreementHistoryKeys.message, 'Message'],
  [UserAgreementHistoryKeys.wasabiPath, 'Wasabi Path'],
  [UserAgreementHistoryKeys.action, 'Action'],
]);

export const USER_AGREEMENT_HISTORY_LOOKUP: LookupBase[] = Object.values(UserAgreementHistoryKeys).map(value => {
  return { [LookupKeys.value]: value, [LookupKeys.description]: userAgreementHistoryMap.get(value) };
});

export const TITLE_ID = 'title';

export enum UserAgreementManagementKeys {
  title = 'title',
  description = 'description',
  effectiveDate = 'effectiveDate',
  fileVersion = 'fileVersion',
  cloneFileVersion = 'cloneFileVersion',
  agreementType = 'agreementType',
  agreementStatus = 'agreementStatus',
  language = 'language',
  sections = 'sections',
  sourceFileKey = 'sourceFileKey',
}

export class UserAgreementManagement extends BaseModel {
  [UserAgreementManagementKeys.title]: string;
  [UserAgreementManagementKeys.description]: string;
  [UserAgreementManagementKeys.effectiveDate]?: Date;
  [UserAgreementManagementKeys.fileVersion]?: string;
  [UserAgreementManagementKeys.agreementType]: PolicyTypes;
  [UserAgreementManagementKeys.agreementStatus]: UserAgreementStatus;
  [UserAgreementManagementKeys.language]: SupportedLanguages;
  [UserAgreementManagementKeys.sections]?: UserAgreementSection[] = [];
  [UserAgreementManagementKeys.sourceFileKey]?: string;
  [UserAgreementManagementKeys.cloneFileVersion]?: string;
}

export type CurrentUserAgreements = {
  [key in PolicyTypes]: string;
};

export const USER_AGREEMENT_MANAGEMENT_SEARCH_KEYS_CONFIG: ChangeLogsConfig[] = [
  {
    value: UserAgreementManagementKeys.title,
    description: 'Document Title',
  },
  {
    value: UserAgreementManagementKeys.description,
    description: 'Introduction Text',
  },
  {
    value: UserAgreementManagementKeys.effectiveDate,
    description: 'Effective Date',
    dataType: ChangeLogsDataType.localDateTimeString,
  },
  {
    value: UserAgreementManagementKeys.fileVersion,
    description: 'File Version',
  },
  {
    value: UserAgreementManagementKeys.agreementType,
    description: 'Type',
    valueLookup: POLICY_TYPES_LOOKUP,
  },
  {
    value: UserAgreementManagementKeys.agreementStatus,
    description: 'Status',
    valueLookup: USER_AGREEMENT_STATUSES_LOOKUP,
  },
  {
    value: UserAgreementManagementKeys.language,
    description: 'Language',
    valueLookup: SUPPORTED_LANGUAGES_LOOKUP,
  },
  {
    value: UserAgreementManagementKeys.sourceFileKey,
    description: 'Source File',
  },
  {
    value: UserAgreementManagementKeys.sections,
    description: 'Sections',
  },
];

export const USER_AGREEMENT_SECTIONS_SEARCH_KEYS_CONFIG: ChangeLogsConfig[] = [
  {
    value: UserAgreementSectionKeys.title,
    description: 'Title',
  },
  {
    value: UserAgreementSectionKeys.description,
    description: 'Text',
  },
];
